import type { ErrorCode } from '@high-crm/shared';
import { config } from './config';

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: ErrorCode, message: string, readonly details?: unknown) {
    super(message);
  }
}

type TokenGetter = () => Promise<string | null>;
let getToken: TokenGetter = async () => null;
let currentOrgId: string | null = null;

export function setTokenGetter(fn: TokenGetter) { getToken = fn; }
export function setCurrentOrg(id: string | null) { currentOrgId = id; }
export function getCurrentOrg() { return currentOrgId; }

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  org?: string | null | false;
  raw?: boolean;
  /** false: send no access token (sign-in endpoints). */
  auth?: boolean;
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${config.apiUrl}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const headers: Record<string, string> = {};
  const token = opts.auth === false ? null : await getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const org = opts.org === false ? null : opts.org ?? currentOrgId;
  if (org) headers['x-organization-id'] = org;
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(url, { method: opts.method ?? 'GET', headers, body });
  } catch {
    throw new ApiError(0, 'INTERNAL', 'Network error');
  }
  if (opts.raw) {
    if (!res.ok) throw new ApiError(res.status, 'INTERNAL', res.statusText);
    return res as unknown as T;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as { error?: { code?: ErrorCode; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'INTERNAL', err?.message ?? res.statusText, err?.details);
  }
  return data as T;
}

export const api = {
  get: <T = unknown>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  post: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }),
  put: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ?? {} }),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: body ?? {} }),
  delete: <T = unknown>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T = unknown>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
};

/** Downloads a file returned by the API (exports) with the auth header. */
export async function download(path: string, query?: RequestOptions['query']) {
  const res = await request<Response>(path, { query, raw: true });
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'export';
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
